import { NS, type NamespaceTuple } from '@ownyou/shared-types';
import type {
  DataSource,
  DataSourceId,
  DataSourceProvider,
  DataSourceStatus,
  DataSourceType,
} from '../../contexts/DataSourceContext';

export const SOURCE_AUTH_OUTCOME_PREFIX = 'source_auth_';

export type SourceAuthOutcomeState = 'connected' | 'needs_reauth' | 'disconnected';

export type SourceAuthOutcomeReason =
  | 'refresh_expired'
  | 'refresh_revoked'
  | 'missing_refresh_token'
  | 'user_disconnected';

export interface SourceAuthOutcome {
  sourceId: DataSourceId;
  state: SourceAuthOutcomeState;
  reason?: SourceAuthOutcomeReason;
  updatedAt: number;
}

export type SourceReadModelId = DataSourceId | 'location';

export type SourceReadModelDisplayState =
  | 'never_connected'
  | 'connected'
  | 'needs_reauth'
  | 'disconnected'
  | 'in_progress'
  | 'problem';

export interface SourceReadModel {
  id: SourceReadModelId;
  label: string;
  provider: DataSourceProvider | 'location';
  type: DataSourceType | 'location';
  displayState: SourceReadModelDisplayState;
  dataSourceStatus?: DataSourceStatus;
  authOutcome?: SourceAuthOutcome;
  lastSync?: Date;
  itemCount?: number;
  error?: string;
  requiresSourceAuthOutcome: boolean;
  supportsReauthRoute: boolean;
  permissions: readonly string[];
  recentActivityLabels: readonly string[];
}

interface StoreLike {
  get<T>(namespace: NamespaceTuple, key: string): Promise<T | null>;
  put<T>(namespace: NamespaceTuple, key: string, value: T): Promise<void>;
}

interface SourceAuthOutcomeWriteInput {
  store: Pick<StoreLike, 'put'>;
  userId: string;
  sourceId: DataSourceId;
  now?: number;
}

interface NeedsReauthSourceAuthOutcomeWriteInput extends SourceAuthOutcomeWriteInput {
  reason: Extract<
    SourceAuthOutcomeReason,
    'refresh_expired' | 'refresh_revoked' | 'missing_refresh_token'
  >;
}

interface StoredTokenData {
  accessToken?: string;
  expiresAt?: number | string;
}

interface StoredSyncMetadata {
  itemCount?: number;
  lastSync?: string;
}

interface SourceDescriptor {
  id: SourceReadModelId;
  label: string;
  provider: DataSourceProvider | 'location';
  type: DataSourceType | 'location';
  requiresSourceAuthOutcome: boolean;
  supportsReauthRoute: boolean;
  permissions: readonly string[];
  recentActivityLabels: readonly string[];
}

export const SOURCE_DESCRIPTORS: readonly SourceDescriptor[] = [
  {
    id: 'gmail',
    label: 'Gmail',
    provider: 'google',
    type: 'email',
    requiresSourceAuthOutcome: true,
    supportsReauthRoute: true,
    permissions: ['Read email metadata', 'Read email message content for local analysis'],
    recentActivityLabels: ['Email sync metadata', 'Interest classifications'],
  },
  {
    id: 'outlook',
    label: 'Outlook',
    provider: 'microsoft',
    type: 'email',
    requiresSourceAuthOutcome: true,
    supportsReauthRoute: true,
    permissions: ['Read email metadata', 'Read email message content for local analysis'],
    recentActivityLabels: ['Email sync metadata', 'Interest classifications'],
  },
  {
    id: 'google-calendar',
    label: 'Google Calendar',
    provider: 'google',
    type: 'calendar',
    requiresSourceAuthOutcome: true,
    supportsReauthRoute: true,
    permissions: ['Read calendar events'],
    recentActivityLabels: ['Calendar event metadata'],
  },
  {
    id: 'microsoft-calendar',
    label: 'Microsoft Calendar',
    provider: 'microsoft',
    type: 'calendar',
    requiresSourceAuthOutcome: true,
    supportsReauthRoute: true,
    permissions: ['Read calendar events'],
    recentActivityLabels: ['Calendar event metadata'],
  },
  {
    id: 'plaid',
    label: 'Plaid',
    provider: 'plaid',
    type: 'financial',
    requiresSourceAuthOutcome: true,
    supportsReauthRoute: true,
    permissions: ['Read account and transaction metadata'],
    recentActivityLabels: ['Financial transaction metadata'],
  },
  {
    id: 'chrome-history',
    label: 'Browser History',
    provider: 'browser-extension',
    type: 'browsing',
    requiresSourceAuthOutcome: false,
    supportsReauthRoute: false,
    permissions: ['Read browser history from local extension permission'],
    recentActivityLabels: ['Local browsing metadata'],
  },
  {
    id: 'location',
    label: 'Location',
    provider: 'location',
    type: 'location',
    requiresSourceAuthOutcome: false,
    supportsReauthRoute: false,
    permissions: ['Read browser location permission or saved local location'],
    recentActivityLabels: ['Local permission state'],
  },
] as const;

export function sourceAuthOutcomeKey(sourceId: DataSourceId): string {
  return `${SOURCE_AUTH_OUTCOME_PREFIX}${sourceId}`;
}

export function sourceRequiresAuthOutcome(sourceId: DataSourceId): boolean {
  return SOURCE_DESCRIPTORS.some(
    descriptor => descriptor.id === sourceId && descriptor.requiresSourceAuthOutcome,
  );
}

export async function writeConnectedSourceAuthOutcome(input: SourceAuthOutcomeWriteInput): Promise<void> {
  await writeSourceAuthOutcome({
    ...input,
    state: 'connected',
  });
}

export async function writeNeedsReauthSourceAuthOutcome(
  input: NeedsReauthSourceAuthOutcomeWriteInput,
): Promise<void> {
  await writeSourceAuthOutcome({
    ...input,
    state: 'needs_reauth',
    reason: input.reason,
  });
}

export async function writeDisconnectedSourceAuthOutcome(input: SourceAuthOutcomeWriteInput): Promise<void> {
  await writeSourceAuthOutcome({
    ...input,
    state: 'disconnected',
    reason: 'user_disconnected',
  });
}

async function writeSourceAuthOutcome(input: SourceAuthOutcomeWriteInput & {
  state: SourceAuthOutcomeState;
  reason?: SourceAuthOutcomeReason;
}): Promise<void> {
  if (!sourceRequiresAuthOutcome(input.sourceId)) return;

  const outcome: SourceAuthOutcome = {
    sourceId: input.sourceId,
    state: input.state,
    updatedAt: input.now ?? Date.now(),
  };
  if (input.reason) {
    outcome.reason = input.reason;
  }

  await input.store.put<SourceAuthOutcome>(
    NS.uiPreferences(input.userId),
    sourceAuthOutcomeKey(input.sourceId),
    outcome,
  );
}

export async function loadSourceReadModels(input: {
  store: StoreLike;
  userId: string;
  dataSources: readonly DataSource[];
  now?: number;
}): Promise<SourceReadModel[]> {
  const dataSourceById = new Map(input.dataSources.map(source => [source.id, source]));

  return Promise.all(
    SOURCE_DESCRIPTORS.map(descriptor =>
      loadSourceReadModel({
        ...input,
        descriptor,
        dataSource: descriptor.id === 'location'
          ? undefined
          : dataSourceById.get(descriptor.id),
      }),
    ),
  );
}

async function loadSourceReadModel(input: {
  store: StoreLike;
  userId: string;
  dataSource?: DataSource;
  descriptor: SourceDescriptor;
  now?: number;
}): Promise<SourceReadModel> {
  const namespace = NS.uiPreferences(input.userId);
  const now = input.now ?? Date.now();
  const sourceId = input.descriptor.id;
  const syncMeta = sourceId === 'location'
    ? null
    : await input.store.get<StoredSyncMetadata>(namespace, `sync_${sourceId}`);

  if (sourceId === 'location') {
    return {
      ...input.descriptor,
      displayState: 'never_connected',
    };
  }

  const authOutcome = await input.store.get<SourceAuthOutcome>(
    namespace,
    sourceAuthOutcomeKey(sourceId),
  );
  const token = await input.store.get<StoredTokenData>(namespace, `oauth_${sourceId}`);
  const displayState = await deriveDisplayStateAndBackfill({
    store: input.store,
    namespace,
    descriptor: input.descriptor,
    sourceId,
    dataSource: input.dataSource,
    authOutcome,
    token,
    now,
  });

  return {
    ...input.descriptor,
    displayState,
    dataSourceStatus: input.dataSource?.status,
    authOutcome: authOutcome ?? undefined,
    lastSync: parseDate(syncMeta?.lastSync),
    itemCount: syncMeta?.itemCount,
    error: input.dataSource?.error,
  };
}

async function deriveDisplayStateAndBackfill(input: {
  store: StoreLike;
  namespace: NamespaceTuple;
  descriptor: SourceDescriptor;
  sourceId: DataSourceId;
  dataSource?: DataSource;
  authOutcome: SourceAuthOutcome | null;
  token: StoredTokenData | null;
  now: number;
}): Promise<SourceReadModelDisplayState> {
  const runtimeStatus = input.dataSource?.status;
  if (runtimeStatus === 'connecting' || runtimeStatus === 'syncing') {
    return 'in_progress';
  }

  if (input.authOutcome) {
    return input.authOutcome.state;
  }

  if (hasUsableToken(input.token, input.now)) {
    if (input.descriptor.requiresSourceAuthOutcome) {
      const newerOutcome = await backfillConnectedOutcome(input);
      if (newerOutcome) {
        return newerOutcome.state;
      }
    }
    return 'connected';
  }

  if (runtimeStatus === 'connected') {
    return 'connected';
  }

  if (runtimeStatus === 'error') {
    return 'problem';
  }

  return 'never_connected';
}

async function backfillConnectedOutcome(input: {
  store: StoreLike;
  namespace: NamespaceTuple;
  sourceId: DataSourceId;
  now: number;
}): Promise<SourceAuthOutcome | null> {
  const key = sourceAuthOutcomeKey(input.sourceId);
  const existing = await input.store.get<SourceAuthOutcome>(input.namespace, key);
  if (existing) return existing;

  await input.store.put<SourceAuthOutcome>(input.namespace, key, {
    sourceId: input.sourceId,
    state: 'connected',
    updatedAt: input.now,
  });
  return null;
}

function hasUsableToken(token: StoredTokenData | null, now: number): boolean {
  if (!token?.accessToken) return false;
  if (!token.expiresAt) return true;

  const expiresAtMs = typeof token.expiresAt === 'string'
    ? new Date(token.expiresAt).getTime()
    : token.expiresAt;
  return Number.isFinite(expiresAtMs) && expiresAtMs > now;
}

function parseDate(value: string | undefined): Date | undefined {
  if (!value) return undefined;
  const time = new Date(value);
  return Number.isNaN(time.getTime()) ? undefined : time;
}
